/**
 * Nachträgliches Einfärben von **Labels** im gerenderten OSMD-SVG: auto-erkannte Akkordsymbole
 * (grün) und die aus Handarbeit übernommenen Bass-Silben (dunkelblau).
 *
 * **Warum es diesen Nachlauf überhaupt gibt (kritisch – nicht durch ein XML-Attribut ersetzen):**
 * OSMD liest das `color`-Attribut an genau vier Stellen – `<stem>`, `<notehead>`, `<note>` und
 * Expression-`<words>`. **`<harmony>` und `<lyric>` sind nicht dabei.** Ihre Labels bekommen
 * deshalb nie eine XML-Farbe und fallen auf `DefaultColorChordSymbol` (nicht gesetzt → gilt
 * `DefaultColorLabel`, in dieser App `'gray'`) bzw. `DefaultColorLyrics` zurück. Der grüne
 * „auto-erkannt"-Marker und die dunkelblaue Handarbeit-Markierung sind am Bildschirm ohne
 * diesen Durchlauf unsichtbar – im MusicXML-Export (MuseScore/PDF) dagegen von Haus aus farbig.
 *
 * **Kein Restore-Stack**, anders als die Farb-Layer in `useScoreHighlight`: jedes `render()` baut
 * Labels und SVG-Knoten neu auf (`reCalculate()` + neuer Drawer), es gibt also nichts
 * zurückzunehmen. Deshalb genügt „nach jedem Render neu malen". Kollisionsfrei zu den
 * Highlight-Layern: die färben `path`/`ellipse`/`rect` innerhalb der StaveNote-Gruppen, dieser
 * Durchlauf `<text>` in Geschwistergruppen.
 */
import {
    GraphicalVoiceEntry,
    type OpenSheetMusicDisplay,
} from 'opensheetmusicdisplay';
import {toValue, type MaybeRefOrGetter} from 'vue';

import {
    handwrittenBassLyricTargets,
    inferredChordSymbolTargets,
    type LabelColorTarget,
    type LyricColorTarget,
} from '@/composables/musicXmlDom';

export interface ScoreLabelColorsDeps {
    getOsmd: () => OpenSheetMusicDisplay | null;
    musicXml: MaybeRefOrGetter<string | null | undefined>;
}

/** Schlüssel eines Takt-Labels (Akkordsymbol). */
function measureKey(staffIndex: number, measureIndex: number): string {
    return `${staffIndex}:${measureIndex}`;
}

/** Schlüssel eines Noten-Labels (Bass-Silbe). */
function noteKeyOf(t: LyricColorTarget): string {
    return `${t.staffIndex}:${t.measureIndex}:${t.voice}:${t.voiceNoteIndex}`;
}

/**
 * Färbt die `<text>`-Knoten eines Labels.
 *
 * VexFlow schreibt `fill` als **Präsentationsattribut auf das `<text>`-Kind**; ein `style.fill` auf
 * der umgebenden `<g>` verlöre dagegen gegen dieses Attribut. Inline-`style` statt
 * `setAttribute('fill')` aus demselben Grund wie in `useScoreHighlight.colorNote`.
 */
function paintLabel(svgNode: Node | undefined, color: string): void {
    // `SVGNode` ist `undefined`, wenn das Label außerhalb des sichtbaren Bereichs liegt oder
    // leeren Text hat (OSMD zeichnet es dann gar nicht erst).
    const group = svgNode as SVGGElement | undefined;
    if (!group?.querySelectorAll) return;
    for (const text of group.querySelectorAll('text')) {
        (text as SVGElement).style.fill = color;
    }
}

export function useScoreLabelColors({getOsmd, musicXml}: ScoreLabelColorsDeps) {
    let cachedXml: string | null = null;
    let chordTargets = new Map<string, string>();
    let lyricTargets = new Map<string, string>();

    /** Die Adresslisten neu ableiten – nur wenn sich das MusicXML geändert hat (ein Parse je
     *  Dokument statt je Render; dasselbe Sparmuster wie `lastLoadedXml` beim Laden). */
    function refreshTargets(xml: string): void {
        if (xml === cachedXml) return;
        cachedXml = xml;
        chordTargets = new Map(
            inferredChordSymbolTargets(xml).map((t: LabelColorTarget) => [
                measureKey(t.staffIndex, t.measureIndex), t.color,
            ]),
        );
        lyricTargets = new Map(
            handwrittenBassLyricTargets(xml).map((t) => [noteKeyOf(t), t.color]),
        );
    }

    /**
     * Nach **jedem** Render aufrufen (idempotent). Ohne Treffer passiert nichts – insbesondere wird
     * nichts auf eine Default-Farbe zurückgesetzt, denn nach dem Render steht sie ohnehin.
     */
    function applyLabelColors(): void {
        const xml = toValue(musicXml) ?? '';
        refreshTargets(xml);
        if (!chordTargets.size && !lyricTargets.size) return;

        const osmd = getOsmd();
        if (!osmd?.GraphicSheet?.MeasureList) return;

        try {
            for (const measureRow of osmd.GraphicSheet.MeasureList) {
                for (const measure of measureRow) {
                    if (!measure) continue;
                    const staffIndex = measure.ParentStaff?.idInMusicSheet ?? 0;
                    const measureIndex = measure.parentSourceMeasure?.measureListIndex ?? -1;
                    const chordColor = chordTargets.get(measureKey(staffIndex, measureIndex));

                    // Position innerhalb der Stimme mitzählen – dieselbe Zählung wie
                    // `findGraphicalNoteByVoiceIndex` und wie `getVoiceNotes` auf der XML-Seite.
                    const perVoiceCount = new Map<number, number>();
                    for (const staffEntry of measure.staffEntries || []) {
                        if (chordColor) {
                            for (const container of staffEntry.graphicalChordContainers || []) {
                                paintLabel(container.GraphicalLabel?.SVGNode, chordColor);
                            }
                        }

                        for (const voiceEntry of staffEntry.graphicalVoiceEntries || []) {
                            const voice = (voiceEntry as GraphicalVoiceEntry)
                                .parentVoiceEntry?.ParentVoice?.VoiceId ?? 1;
                            const first = perVoiceCount.get(voice) ?? 0;
                            perVoiceCount.set(voice, first + (voiceEntry.notes?.length ?? 0));
                            if (!lyricTargets.size) continue;

                            // Die Lyric hängt am VoiceEntry, im XML dagegen an dessen ERSTER Note –
                            // deshalb zählt `first` (Index der Akkord-Primärnote), nicht der Zähler
                            // nach dem Hochzählen.
                            const color = lyricTargets.get(
                                `${staffIndex}:${measureIndex}:${voice}:${first}`,
                            );
                            if (!color) continue;
                            for (const lyric of staffEntry.LyricsEntries || []) {
                                if (lyric.LyricsEntry?.Parent !== voiceEntry.parentVoiceEntry) continue;
                                paintLabel(lyric.GraphicalLabel?.SVGNode, color);
                            }
                        }
                    }
                }
            }
        } catch (e) {
            console.warn('[OSMD] applyLabelColors failed', e);
        }
    }

    return {applyLabelColors} as const;
}

export type ScoreLabelColors = ReturnType<typeof useScoreLabelColors>;
